import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LeagueSettingsLayout } from '@/components/LeagueSettingsLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { InviteMemberModal } from '@/components/InviteMemberModal';
import {
  Shield,
  Crown,
  User,
  Loader2,
  UserPlus,
  MoreVertical,
  Mail,
  Clock,
  X,
  Trash2,
  UserCog,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface LeagueMember {
  id: string;
  user_id: string;
  display_name: string;
  role: 'owner' | 'admin' | 'member';
  status: string;
  joined_at: string;
  last_active_at: string | null;
  magic_link_sent_at: string | null;
  email: string | null;
}

interface PendingInvite {
  id: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  created_at: string;
  expires_at: string;
}

export default function LeagueMembers() {
  const { user, currentMembership, isSuperAdmin } = useAuth();
  const { toast } = useToast();

  const [members, setMembers] = useState<LeagueMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<LeagueMember | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [magicLoginLoading, setMagicLoginLoading] = useState<string | null>(null);

  const canEdit =
    isSuperAdmin ||
    currentMembership?.role === 'owner' ||
    currentMembership?.role === 'admin';

  const isOwner = currentMembership?.role === 'owner' || isSuperAdmin;

  useEffect(() => {
    if (currentMembership?.league_id) {
      loadMembers();
      loadPendingInvites();
    } else {
      const timeout = setTimeout(() => { if (!currentMembership?.league_id) setLoading(false); }, 3000);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership?.league_id]);

  const loadMembers = async () => {
    if (!currentMembership?.league_id) return;
    try {
      const { data, error } = await supabase.rpc('get_league_members', { p_league_id: currentMembership.league_id });
      if (error) { console.error('Error loading members:', error); return; }
      setMembers((data || []) as LeagueMember[]);
    } catch (e) { console.error('Exception loading members:', e); }
    setLoading(false);
  };

  const loadPendingInvites = async () => {
    if (!currentMembership?.league_id || !canEdit) return;
    try {
      const { data, error } = await supabase
        .from('league_invites')
        .select('id, email, role, created_at, expires_at')
        .eq('league_id', currentMembership.league_id)
        .is('accepted_at', null)
        .order('created_at', { ascending: false });
      if (error) { console.error('Error loading invites:', error); return; }
      setPendingInvites((data || []) as PendingInvite[]);
    } catch (e) { console.error('Exception loading invites:', e); }
  };

  const handleRoleChange = async (memberId: string, newRole: 'admin' | 'member') => {
    if (!canEdit) return;
    setActionLoading(true);
    const { error } = await supabase.from('league_memberships').update({ role: newRole }).eq('id', memberId);
    if (error) { toast({ title: 'Failed to update role', description: error.message, variant: 'destructive' }); }
    else { toast({ title: 'Role updated' }); loadMembers(); }
    setActionLoading(false);
  };

  const handleRemoveMember = async () => {
    if (!selectedMember || !canEdit) return;
    setActionLoading(true);
    const { error } = await supabase.from('league_memberships').update({ status: 'left' }).eq('id', selectedMember.id);
    if (error) { toast({ title: 'Failed to remove member', description: error.message, variant: 'destructive' }); }
    else { toast({ title: 'Member removed' }); loadMembers(); }
    setActionLoading(false);
    setRemoveMemberDialogOpen(false);
    setSelectedMember(null);
  };

  const handleCancelInvite = async (inviteId: string) => {
    setActionLoading(true);
    const { error } = await supabase.from('league_invites').delete().eq('id', inviteId);
    if (error) { toast({ title: 'Failed to cancel invite', description: error.message, variant: 'destructive' }); }
    else { toast({ title: 'Invite cancelled' }); loadPendingInvites(); }
    setActionLoading(false);
  };

  const handleSendMagicLogin = async (member: LeagueMember) => {
    if (!currentMembership?.league_id) return;
    setMagicLoginLoading(member.id);
    try {
      const { error } = await supabase.functions.invoke('send-magic-login', {
        body: { userId: member.user_id, leagueId: currentMembership.league_id },
      });
      if (error) { toast({ title: 'Failed to send magic login', description: error.message, variant: 'destructive' }); }
      else {
        toast({ title: `Magic login email sent to ${member.display_name}` });
        setMembers((prev) => prev.map((m) => m.id === member.id ? { ...m, magic_link_sent_at: new Date().toISOString() } : m));
      }
    } catch (e) { console.error('Exception sending magic login:', e); toast({ title: 'Failed to send magic login', description: 'An unexpected error occurred', variant: 'destructive' }); }
    setMagicLoginLoading(null);
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return <Badge className="bg-amber-500 text-white"><Crown className="h-3 w-3 mr-1" />Owner</Badge>;
      case 'admin':
        return <Badge className="bg-blue-500 text-white"><Shield className="h-3 w-3 mr-1" />Admin</Badge>;
      default:
        return <Badge variant="secondary"><User className="h-3 w-3 mr-1" />Member</Badge>;
    }
  };

  const isInviteExpired = (expiresAt: string) => new Date(expiresAt) < new Date();

  if (!currentMembership) {
    return (
      <LeagueSettingsLayout activeTab="members">
        <p className="text-stone-600">No league selected</p>
      </LeagueSettingsLayout>
    );
  }

  if (loading) {
    return (
      <LeagueSettingsLayout activeTab="members">
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        </div>
      </LeagueSettingsLayout>
    );
  }

  return (
    <LeagueSettingsLayout activeTab="members">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>League Members</CardTitle>
            <CardDescription>
              {members.length} active member{members.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          {canEdit && (
            <Button onClick={() => setInviteModalOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {members.map((member) => (
              <div key={member.id} className="p-3 bg-stone-50 rounded-lg space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 shrink-0 bg-stone-200 rounded-full flex items-center justify-center text-stone-600 font-medium">
                      {member.display_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{member.display_name}</p>
                      {member.email && <p className="text-sm text-stone-500 truncate">{member.email}</p>}
                    </div>
                  </div>
                  {canEdit && member.role !== 'owner' && member.user_id !== user?.id && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleSendMagicLogin(member)} disabled={magicLoginLoading === member.id}>
                          <Mail className="mr-2 h-4 w-4" />
                          {magicLoginLoading === member.id ? 'Sending...' : 'Send Magic Login'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {isOwner && (
                          <>
                            <DropdownMenuItem
                              onClick={() => handleRoleChange(member.id, member.role === 'admin' ? 'member' : 'admin')}
                              disabled={actionLoading}
                            >
                              <UserCog className="mr-2 h-4 w-4" />
                              {member.role === 'admin' ? 'Demote to Member' : 'Promote to Admin'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem
                          onClick={() => { setSelectedMember(member); setRemoveMemberDialogOpen(true); }}
                          disabled={actionLoading}
                          className="text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove from League
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <div className="flex items-center gap-2 pl-[52px]">
                  {getRoleBadge(member.role)}
                  <span className="text-xs text-stone-400">
                    {member.last_active_at
                      ? `Active ${formatDistanceToNow(new Date(member.last_active_at), { addSuffix: true })}`
                      : 'Never active'}
                  </span>
                  {member.magic_link_sent_at && (() => {
                    const sentAt = new Date(member.magic_link_sent_at);
                    const minutesAgo = (Date.now() - sentAt.getTime()) / 60000;
                    const isValid = minutesAgo < 60;
                    return (
                      <span className={`text-xs ${isValid ? 'text-emerald-600' : 'text-stone-400'}`}>
                        {isValid
                          ? `Magic link sent ${formatDistanceToNow(sentAt, { addSuffix: true })}`
                          : 'Magic link expired'}
                      </span>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>

          {canEdit && pendingInvites.length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Pending Invites ({pendingInvites.length})
              </h3>
              <div className="space-y-2">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className={`p-3 rounded-lg space-y-2 ${
                      isInviteExpired(invite.expires_at)
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-amber-50 border border-amber-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 shrink-0 bg-stone-200 rounded-full flex items-center justify-center text-stone-500">
                          <Mail className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{invite.email}</p>
                          <p className="text-xs text-stone-500">
                            {isInviteExpired(invite.expires_at) ? (
                              <span className="text-red-600">Expired</span>
                            ) : (
                              <>Expires {new Date(invite.expires_at).toLocaleDateString()}</>
                            )}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-stone-500 hover:text-red-600"
                        onClick={() => handleCancelInvite(invite.id)}
                        disabled={actionLoading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="pl-[52px]">
                      {getRoleBadge(invite.role)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {currentMembership?.league_id && (
        <InviteMemberModal
          open={inviteModalOpen}
          onOpenChange={setInviteModalOpen}
          leagueId={currentMembership.league_id}
          leagueName={currentMembership.league?.name ?? 'League'}
          onInviteSent={() => loadPendingInvites()}
        />
      )}

      <AlertDialog open={removeMemberDialogOpen} onOpenChange={setRemoveMemberDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selectedMember?.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this member from the league? Their predictions and history will be preserved, but they'll need a new invite to rejoin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember} disabled={actionLoading} className="bg-red-600 hover:bg-red-700">
              {actionLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Removing...</> : 'Remove Member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </LeagueSettingsLayout>
  );
}
